/**
 * Error Boundary Component for TanStack Router
 *
 * Handles errors thrown during route rendering, loaders, or beforeLoad.
 * Renders appropriate error UI inline based on HTTP status codes.
 *
 * IMPORTANT: This component avoids using useRouter() during render to prevent
 * "Cannot update a component while rendering a different component" errors.
 * Instead, it renders error UI inline and uses a separate component for redirects.
 */
import { useEffect, useState } from 'react';
import { Navigate } from '@tanstack/react-router';

import { ErrorPage } from './ErrorPage';

import type { ApiError } from '@/lib/apiErrors';

/**
 * Checks if an error is related to API connection issues (network-level errors)
 *
 * Browser-specific messages:
 * - Chrome: "TypeError: Failed to fetch"
 * - Firefox: "TypeError: NetworkError when attempting to fetch resource"
 * - Safari: "TypeError: Load failed"
 */
function isNetworkError(error: Error): boolean {
  const connectionErrorPatterns = [
    /network\s*error/i,
    /connection\s*refused/i,
    /cannot\s*connect/i,
    /ERR_CONNECTION/i,
    /ERR_NETWORK/i,
    /^(TypeError:\s*)?Failed to fetch$/i,
    /NetworkError when attempting to fetch resource/i,
    /^(TypeError:\s*)?Load failed$/i,
  ];

  return connectionErrorPatterns.some((pattern) => pattern.test(error.message));
}

/**
 * Component that handles deferred redirect to avoid setState during render.
 */
function DeferredRedirect({ to, search }: { to: string; search?: Record<string, string> }) {
  const [shouldRedirect, setShouldRedirect] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShouldRedirect(true), 0);
    return () => clearTimeout(timer);
  }, []);

  if (shouldRedirect) {
    return <Navigate to={to} search={search} replace />;
  }

  return null;
}

/**
 * Error Boundary Component for TanStack Router
 *
 * Uses the unified ErrorPage component for all error states.
 * Handles special cases like 401 redirect and network errors.
 */
export function ErrorBoundary({ error }: { error: Error }) {
  // Extract error properties (supports both ApiError and regular Error)
  const apiError = error as Error & Partial<ApiError>;
  const status = apiError.status;
  const message = error.message || 'An unexpected error occurred';
  const stack = error.stack;
  const requestId = apiError.requestId;

  // Handle 401 - Redirect to login
  if (status === 401) {
    return <DeferredRedirect to="/login" search={{ from: window.location.pathname }} />;
  }

  // Handle network errors as 503
  if (isNetworkError(error)) {
    return (
      <ErrorPage
        status={503}
        title="Connection Error"
        message="Unable to connect to the server. Please check your internet connection."
      />
    );
  }

  // Handle all other errors with unified ErrorPage
  return (
    <ErrorPage
      status={status}
      message={message}
      stack={stack}
      requestId={requestId}
    />
  );
}
